import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import accounting from 'accounting';
import { showToast } from 'stores/ToastStore';
import { titleize } from 'underscore.string';
import _ from 'lodash';
import Pagination from 'components/v2/PaginationV2';
import Table from 'components/v2/Table';
import Badge from 'components/v2/Badge';
import EditButton from 'components/v2/buttons/EditButton';
import {
  getMeteredAccessorials,
  updateStorageMeteringRate,
  updateStorageMeteringFreeDays,
} from 'utils/MeteringAPIUtils';
import Spinner from 'components/ui/Spinner';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import ShipmentStore from 'stores/ShipmentStore';
import MarketShipperRateModel from 'components/modules/shipments/MarketShipperRateModel';
import { findUsers } from 'utils/UserWebAPIUtils';

const Loading = function () {
  return (
    <div className="absolute justify-center w-full" style={{ top: '50%' }}>
      <div className="align-middle">
        <span className="text-5xl text-green">
          <Spinner showMessage={false} />
        </span>
      </div>
    </div>
  );
};

const METERING_LIMIT = 5;

function getColor(payeeType) {
  if (payeeType === 'vendor') {
    return 'blue';
  }
  return 'gray';
}

export const createColumns = (
  history,
  t,
  setShowActionModel,
  setStopIdForMarketShipperRateModal,
  setRate,
  setFreeDays,
  vendors = {},
) => {
  return [
    {
      field: 'charge',
      label: t('CHARGE'),
    },
    {
      field: 'container',
      label: t('CONTAINER'),
    },
    {
      field: 'meteringDates',
      label: t('METERING_DATES'),
      render: (_, row) => {
        const startDate = row?.meteringStart
          ? moment(row.meteringStart).format('M/D/YYYY')
          : 'TBD';
        const endDate = row?.meteringEnd
          ? moment(row.meteringEnd).format('M/D/YYYY')
          : 'TBD';
        return `${startDate} - ${endDate}`;
      },
    },
    {
      field: 'payeeType',
      label: t('PAYEE_TYPE'),
      render: (payeeType) => {
        return <Badge color={getColor(payeeType)}>{titleize(payeeType)}</Badge>;
      },
    },
    {
      field: 'payee',
      label: t('PAYEE_NAME'),
      render: (vendorId) => {
        return (
          <div data-testid="vendOrIdToVendorName">
            {vendors[vendorId] ? vendors[vendorId]?.account_name : vendorId}
          </div>
        );
      },
    },
    {
      field: 'rate',
      label: t('PAYEE_RATE'),
      render: (rate) => {
        return accounting.formatMoney(
          Number(rate).toFixed(2),
          getCurrencySymbol(DEFAULT_CURRENCY),
        );
      },
    },
    {
      field: 'units',
      label: t('UNITS'),
    },
    {
      field: 'totalCost',
      label: t('TOTAL_COST'),
      render: (_, row) => {
        const total = row.rate * row.units;
        return accounting.formatMoney(
          Number(total).toFixed(2),
          getCurrencySymbol(DEFAULT_CURRENCY),
        );
      },
    },
    {
      field: 'freeDays',
      label: t('FREE_DAYS'),
    },
    {
      field: 'stopId',
      label: '',
      render: (stopId, row) => {
        return (
          <div></div>
          // <EditButton 
          //   onClick={() => {
          //     setRate(row.rate);
          //     setFreeDays(row.freeDays);
          //     setShowActionModel(true);
          //     setStopIdForMarketShipperRateModal(stopId);
          //   }}
          //   color="blue"
          // />
        );
      },
    },
  ];
};

export const setHeaders = (t) => {
  return [
    { field: 'charge', label: t('CHARGE') },
    {
      field: 'container',
      label: t('CONTAINER'),
    },
    {
      field: 'meteringDates',
      label: t('METERING_DATES'),
    },
    {
      field: 'payeeType',
      label: t('PAYEE_TYPE'),
    },
    {
      field: 'payee',
      label: t('PAYEE_NAME'),
    },
    {
      field: 'rate',
      label: t('PAYEE_RATE'),
    },
    {
      field: 'units',
      label: t('UNITS'),
    },
    {
      field: 'totalCost',
      label: t('TOTAL_COST'),
    },
    {
      field: 'freeDays',
      label: t('FREE_DAYS'),
    },
  ];
};

export default function MeteringTable({ shipment }) {
  const { id: shipmentId } = shipment;
  const { t } = useTranslation();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [meteringResponse, setMeteringResponse] = useState({
    accessorialMeterings: [],
    total: 0,
  });
  const [activePage, setActivePage] = useState(1);
  const [payload, setPayload] = useState({
    sortBy: 'id',
    sortOrder: 'ascend',
    skip: null,
    limit: METERING_LIMIT,
    meteringInput: {
      shipmentId,
    },
  });
  const [showActionModel, setShowActionModel] = useState(false);
  const [stopIdForMarketShipperRateModal, setStopIdForMarketShipperRateModal] =
    useState('');

  const [rate, setRate] = useState();
  const [freeDays, setFreeDays] = useState();
  const [vendors, setVendors] = useState({});

  const fetchMetering = async (
    sortBy,
    sortOrder,
    skip,
    limit,
    meteringInput = {
      shipmentId,
    },
  ) => {
    setIsLoading(true);

    const meteringResponse = await getMeteredAccessorials(
      sortBy,
      sortOrder,
      skip,
      limit,
      meteringInput,
    );

    setIsLoading(false);
    if (meteringResponse?.data?.getMeteredAccessorials?.accessorialMeterings) {
      let meteredAccessorials =
        meteringResponse.data.getMeteredAccessorials.accessorialMeterings.map(
          (meteredAccessorial) => {
            let _meteredAccessorial = JSON.parse(
              JSON.stringify(meteredAccessorial),
            );
            let sortedStops = shipment.stops;
            sortedStops = _.sortBy(sortedStops, 'sequence');
            let yardStop = _.find(sortedStops, 'yardId');
            _meteredAccessorial.charge =
              _meteredAccessorial.accessorialType ||
              (yardStop && yardStop.empty
                ? 'Storage - Empty'
                : 'Storage - Full');
            return _meteredAccessorial;
          },
        );
      setMeteringResponse({
        total: meteredAccessorials.length,
        accessorialMeterings: meteredAccessorials,
      });
    }
  };

  // TODO
  // it's preferable to not pollute this component with logic heavy functions BUT due to how the logic works for market shipper rate modal the UI flow needs to be redone
  // that rework is tech debt and a larger ask. just minimizing the coupling between MarketShipperRateModel and MeteringTable and StopCompleteModal respectively in the meantime.
  const handleShowToaster = (error) => {
    if (error) {
      showToast(
        t('UPDATE_MARKET_SHIPPER_RATE_ERROR'),
        t('UPDATE_MARKET_SHIPPER_RATE_FAILED_WITH_ERROR', {
          error: error.message,
        }),
        'error',
      );
      return;
    }
    showToast(
      t('MARKET_SHIPPER_RATE_UPDATED_SUCCESSFULLY'),
      t('MARKET_SHIPPER_RATE_UPDATED_SUCCESSFULLY'),
      'success',
    );
  };

  const saveMarketShipperRateHandler = async (rate, freeDays, shipment) => {
    const { error: rateError } = await updateStorageMeteringRate({
      shipmentRate: rate,
      query: {
        shipmentId: shipment.id,
        shipperId: shipment.shipper.id,
      },
    });

    const { error: freeDaysError } = await updateStorageMeteringFreeDays({
      freeDays,
      query: {
        shipmentId: shipment.id,
        shipperId: shipment.shipper.id,
      },
    });
    handleShowToaster(rateError || freeDaysError);
  };

  useEffect(() => {
    ShipmentStore.addChangeListener(fetchMetering);
    return () => {
      ShipmentStore.removeChangeListener(fetchMetering);
    };
  }, []);

  useEffect(() => {
    fetchMetering(...Object.values(payload));
  }, [payload]);

  useEffect(() => {
    fetchUsers();
  }, [meteringResponse?.accessorialMeterings]);

  async function fetchUsers() {
    setIsLoading(true);
    const venderIds = meteringResponse?.accessorialMeterings?.map(
      (accessorial) => accessorial?.payee,
    );
    try {
      if (venderIds.length) {
        const {
          entity: { items },
        } = await findUsers({ _id: venderIds });
        const vendors = items.reduce((_vendors, _vendor) => {
          _vendors[_vendor._id] = {
            account_name:
              _vendor._type === 'Carrier' ? _vendor.name : _vendor.account_name,
          };
          return _vendors;
        }, {});
        setVendors(vendors);
      }
    } catch (error) {
      showToast(t('NOT_FOUND'), t('USERS_NOT_FOUND'), 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handlePageChange = (eventKey) => {
    setActivePage(eventKey);
    setPayload((oldPayload) => {
      return {
        ...oldPayload,
        skip: METERING_LIMIT * (eventKey - 1),
      };
    });
  };

  const renderFooter = () => {
    return (
      <tr>
        <td colSpan={10} className="p-0">
          <Pagination
            className="border-t-0"
            perPage={METERING_LIMIT}
            page={activePage}
            pages={Math.ceil(meteringResponse.total / METERING_LIMIT)}
            total={meteringResponse.total}
            showCount
            paginationContainerClassName="mx-auto my-0 sticky right-2/4"
            onPageClick={(eventKey) => handlePageChange(eventKey)}
          />
        </td>
      </tr>
    );
  };
  return (
    <>
      <div className="overflow-x-auto px-1">
        <h3 className="my-0 mb-4 text-base">{t('METERING')}</h3>
        <div className="mx-auto max-w-8xl 2xl:max-w-screen-2xl pb-4 pt-2">
          {isLoading ? (
            <Loading />
          ) : (
            <Table
              headers={setHeaders(t)}
              data={meteringResponse.accessorialMeterings}
              columns={createColumns(
                history,
                t,
                setShowActionModel,
                setStopIdForMarketShipperRateModal,
                setRate,
                setFreeDays,
                vendors,
              )}
              footer={renderFooter}
            />
          )}
        </div>
      </div>
      {showActionModel && (
        <MarketShipperRateModel
          showModal={showActionModel}
          setShowActionModel={setShowActionModel}
          shipment={shipment}
          onSave={saveMarketShipperRateHandler}
          curRate={rate}
          curFreeDays={freeDays}
        />
      )}
    </>
  );
}
